import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Rocket, Play, X, Gauge, AlertTriangle, CalendarClock, Bell, ChevronDown, ChevronRight } from "lucide-react";

const KIND_ICONS = { nearing_complete: Bell, predicted_late: AlertTriangle, trueup_overdue: CalendarClock };
const KIND_COLORS = { nearing_complete: "#0044FF", predicted_late: "#EF4444", trueup_overdue: "#F59E0B" };

export default function PilItPanel({ projectId, pilit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [speedsOpen, setSpeedsOpen] = useState(false);
  const [open, setOpen] = useState(() => localStorage.getItem("dashpanel-pilit-panel") !== "0");
  const togglePanel = () => setOpen((o) => { localStorage.setItem("dashpanel-pilit-panel", o ? "0" : "1"); return !o; });
  const enabled = pilit?.mode === "pilit";

  const toggle = async (on) => {
    try {
      await api.put(`/projects/${projectId}/pilit`, { enabled: on });
      toast.success(on ? "Pilot is ON — the plan will auto-update every working day" : "Pilot is off — you're updating manually");
      onChanged();
    } catch (e) {
      toast.error("Could not change mode");
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/pilit/run`);
      toast.success(`Pilot ran — ${data.new_alerts} new alert${data.new_alerts === 1 ? "" : "s"}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (id) => {
    try {
      await api.patch(`/pilit/alerts/${id}/seen`);
      onChanged();
    } catch (e) { /* noop */ }
  };

  return (
    <div className="border border-border rounded-xl p-4 lg:col-span-2 bg-card" style={{ borderTop: "3px solid #4F46E5" }} data-testid="pilit-panel">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={togglePanel} className="flex items-center gap-2 cursor-pointer" data-testid="pilit-panel-toggle">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <Rocket className="h-4 w-4 text-primary" />
          <h3 className="font-display font-bold text-sm">Auto Pilot</h3>
        </button>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="pilit-toggle" />
        <span className={`text-xs font-bold ${enabled ? "text-primary" : "text-muted-foreground"}`}>{enabled ? "ON" : "OFF"}</span>
        {pilit?.last_run && <span className="text-xs text-muted-foreground">last run {pilit.last_run}</span>}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setSpeedsOpen(true)} data-testid="pilit-speeds-button"><Gauge className="h-3.5 w-3.5" /> Speeds</Button>
          <Button size="sm" className="gap-1.5" onClick={runNow} disabled={busy || !enabled} data-testid="pilit-run-now">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run now
          </Button>
        </div>
      </div>
      {open && (!enabled ? (
        <p className="text-sm text-muted-foreground mt-3">Turn on Pilot to auto-advance % complete every working day based on each person's speed and task pace. Tasks cap at 99% — <strong className="text-foreground">only you can confirm 100%</strong> in a true-up, which also teaches Pilot each person's real velocity.</p>
      ) : (pilit?.alerts || []).length === 0 ? (
        <p className="text-sm text-muted-foreground mt-3" data-testid="pilit-no-alerts">No new alerts. Pilot will flag tasks nearing completion, forecast slips, and overdue true-ups here (and by email).</p>
      ) : (
        <div className="space-y-1.5 mt-3" data-testid="pilit-alerts">
          {pilit.alerts.map((a) => {
            const Icon = KIND_ICONS[a.kind] || Bell;
            return (
              <div key={a.id} className="flex items-start gap-2 text-sm bg-secondary/30 rounded-lg px-3 py-2" data-testid={`pilit-alert-${a.id}`}>
                <Icon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: KIND_COLORS[a.kind] }} />
                <span className="flex-1 min-w-0">{a.message}</span>
                <button onClick={() => dismiss(a.id)} className="text-muted-foreground hover:text-foreground shrink-0" title="Dismiss" data-testid={`pilit-dismiss-${a.id}`}><X className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>
      ))}
      <SpeedsDialog open={speedsOpen} onOpenChange={setSpeedsOpen} projectId={projectId} />
    </div>
  );
}

const FACTOR_DEFS = [
  ["ramping_up", "New to the domain (ramping up)", "×0.85"],
  ["multi_project", "Split across many projects", "×0.9"],
  ["timezone_overhead", "Time-zone / handoff overhead", "×0.9"],
  ["meeting_heavy", "Frequent meetings / on-call", "×0.85"],
];
const PRIORITY_OPTS = [["none", "None ×1.0"], ["light", "Light ×0.9"], ["moderate", "Moderate ×0.75"], ["heavy", "Heavy ×0.5"]];
const AVAIL_OPTS = [100, 75, 50, 25];

function SpeedsDialog({ open, onOpenChange, projectId }) {
  const [people, setPeople] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPeople(null);
    setExpanded(null);
    api.get("/velocity/people", { params: { project_id: projectId } })
      .then(({ data }) => setPeople(data))
      .catch(() => { toast.error("Could not load people"); onOpenChange(false); });
  }, [open, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (person, patch, label) => {
    try {
      const { data } = await api.put("/velocity/person", { person, ...patch });
      setPeople((xs) => xs.map((x) => (x.key === data.key ? { ...x, ...data } : x)));
      if (label) toast.success(label);
    } catch (e) { toast.error("Could not save"); }
  };

  const factorLine = (p) => {
    const parts = [];
    if ((p.availability_pct ?? 100) < 100) parts.push(`${p.availability_pct}% available`);
    if ((p.priority_load || "none") !== "none") parts.push(`${p.priority_load} priorities`);
    if ((p.factors || []).length) parts.push(`${p.factors.length} drag${p.factors.length === 1 ? "" : "s"}`);
    return parts.join(" · ");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="pilit-speeds-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Gauge className="h-5 w-5 text-primary" /> Resource speeds</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Your initial assessment. As you confirm true-ups, Pilot measures real velocity and blends your rating out (shown as "learned"). Availability, competing priorities and other drags multiply on top.</p>
        {!people ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
          <div className="divide-y divide-border max-h-[62vh] overflow-y-auto">
            {people.length === 0 && <p className="text-sm text-muted-foreground py-4">No assigned people yet.</p>}
            {people.map((p) => {
              const kid = p.key.replace(/\s/g, "-");
              const isOpen = expanded === p.key;
              const fl = factorLine(p);
              return (
                <div key={p.key} className="py-2.5" data-testid={`speed-row-${kid}`}>
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{p.person}</p>
                      <p className="text-[11px] text-muted-foreground">
                        velocity <span className="font-bold text-foreground">×{p.effective}</span>
                        {p.samples > 0 && <span className="text-primary font-semibold"> · learned ×{p.measured_velocity} from {p.samples} true-up{p.samples === 1 ? "" : "s"}</span>}
                        {fl && <span className="text-amber-600 font-semibold"> · {fl}</span>}
                      </p>
                    </div>
                    <Select value={p.pm_speed} onValueChange={(v) => save(p.person, { pm_speed: v }, `${p.person} set to ${v}`)}>
                      <SelectTrigger className="h-8 w-28 text-xs" data-testid={`speed-select-${kid}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fast">Fast ×1.25</SelectItem>
                        <SelectItem value="medium">Medium ×1.0</SelectItem>
                        <SelectItem value="slow">Slow ×0.8</SelectItem>
                      </SelectContent>
                    </Select>
                    <button type="button" onClick={() => setExpanded(isOpen ? null : p.key)}
                      className={`text-xs font-semibold inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-md border transition-colors ${isOpen ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"}`}
                      data-testid={`speed-factors-toggle-${kid}`}>
                      Factors {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </button>
                  </div>
                  {isOpen && (
                    <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3 space-y-3" data-testid={`speed-factors-panel-${kid}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground w-24 shrink-0">Availability</span>
                        {AVAIL_OPTS.map((a) => (
                          <button key={a} type="button"
                            onClick={() => save(p.person, { availability_pct: a }, `${p.person}: ${a}% available`)}
                            className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-colors ${(p.availability_pct ?? 100) === a ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                            data-testid={`speed-avail-${kid}-${a}`}>{a}%</button>
                        ))}
                        <span className="text-[10px] text-muted-foreground">part-time = less than 100%</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground w-24 shrink-0">Priorities</span>
                        <Select value={p.priority_load || "none"} onValueChange={(v) => save(p.person, { priority_load: v }, `${p.person}: ${v} competing priorities`)}>
                          <SelectTrigger className="h-7 w-40 text-xs" data-testid={`speed-priority-${kid}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{PRIORITY_OPTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-[10px] text-muted-foreground">work competing outside your projects</span>
                      </div>
                      <div className="space-y-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Other considerations</span>
                        {FACTOR_DEFS.map(([k, label, mult]) => (
                          <label key={k} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input type="checkbox" checked={(p.factors || []).includes(k)}
                              onChange={(e) => {
                                const next = e.target.checked ? [...(p.factors || []), k] : (p.factors || []).filter((x) => x !== k);
                                save(p.person, { factors: next });
                              }}
                              className="h-3.5 w-3.5 accent-[#0044FF]" data-testid={`speed-factor-${kid}-${k}`} />
                            {label} <span className="text-muted-foreground">{mult}</span>
                          </label>
                        ))}
                      </div>
                      <input defaultValue={p.factor_notes || ""} placeholder="Notes — context on this person's pace (optional)"
                        onBlur={(e) => { if (e.target.value !== (p.factor_notes || "")) save(p.person, { factor_notes: e.target.value }, "Notes saved"); }}
                        className="w-full h-8 rounded-md border border-border bg-background px-2 text-xs"
                        data-testid={`speed-notes-${kid}`} />
                      <p className="text-[11px] text-muted-foreground border-t border-border/60 pt-2" data-testid={`speed-math-${kid}`}>
                        Effective speed <b className="text-foreground">×{p.effective}</b> = {p.samples > 0 ? "blended pace" : `${p.pm_speed} pace`} × {(p.availability_pct ?? 100)}% availability × {p.priority_load || "none"} priorities{(p.factors || []).length ? ` × ${p.factors.length} consideration${p.factors.length === 1 ? "" : "s"}` : ""}. Pilot uses this for daily progress and finish-date forecasts.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
